import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faCalendar,
  faCheckCircle,
  faTimesCircle,
  faSearch,
  faEye,
  faTrash,
  faBan,
  faUnlock,
  faLayerGroup,
  faTimes,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import api from '../../services/api';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import './UsersDashboard.css';

const UsersDashboard = () => {
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const canManageUsers = isSuperAdmin() || hasPermission('users.manage');
  const canManageGroups = isSuperAdmin() || hasPermission('groups.manage');
  const canBlockUsers = isSuperAdmin() || hasPermission('users.block');
  const canDeleteUsers = isSuperAdmin() || hasPermission('users.delete');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isGroupsExpanded, setIsGroupsExpanded] = useState(false);
  const [expandedGroupIds, setExpandedGroupIds] = useState([]);

  // Create-espace modal (replaces window.prompt) + toast feedback
  const [espaceModalUser, setEspaceModalUser] = useState(null);
  const [espaceNameInput, setEspaceNameInput] = useState('');
  const [creatingEspace, setCreatingEspace] = useState(false);
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Mobile expanded user row
  const [expandedUserId, setExpandedUserId] = useState(null);
  const toggleUserExpand = (userId) => {
    setExpandedUserId(prev => prev === userId ? null : userId);
  };

  // Check if a user is "new" (created within the last 48 hours)
  const isNewUser = (user) => {
    const createdAt = new Date(user.created_at || user.date_inscription);
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000);
    return createdAt > cutoff;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      const usersData = Array.isArray(response.data) ? response.data : [];
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGroups = async (userId) => {
    setLoadingGroups(true);
    try {
      // Fetching user groups
      const response = await api.get(`/admin/users/${userId}/groups`);
      // Groups loaded
      setUserGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
      setUserGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const reason = window.prompt(
      `Veuillez indiquer la raison de suppression de ${userName}:\n\n(La demande sera envoyée pour approbation ou appliquée immédiatement selon vos droits)`
    );
    
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      alert("La raison est obligatoire pour demander une suppression.");
      return;
    }

    try {
      const response = await api.post('/dashboard-admin/deletion-requests/user', {
        userId,
        reason: reason.trim()
      });
      
      if (response.data.autoApproved) {
        // Super admin or owner - immediate soft delete
        setUsers(users.filter(u => u.iduser !== userId));
        if (selectedUser?.iduser === userId) setSelectedUser(null);
        alert('✅ Utilisateur supprimé (récupérable pendant 6 mois)');
      } else {
        // Regular admin - request sent for approval
        alert('📤 Demande de suppression envoyée pour approbation');
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la demande de suppression');
    }
  };

  const handleBlockUser = async (user) => {
    const newStatus = !user.is_blocked;
    const action = newStatus ? 'bloquer' : 'débloquer';
    let reason = '';

    if (newStatus) {
      reason = window.prompt(`Veuillez saisir le motif du blocage pour ${user.username || user.email} :`);
      if (reason === null) return; // User cancelled
      if (!reason.trim()) {
        alert("Le motif est obligatoire pour bloquer un utilisateur.");
        return;
      }
    } else {
      if (!window.confirm(`Voulez-vous vraiment ${action} cet utilisateur ?`)) return;
    }
    
    try {
      await api.put(`/admin/users/${user.iduser}/block`, { blocked: newStatus, reason });
      setUsers(users.map(u => 
        u.iduser === user.iduser ? { ...u, is_blocked: newStatus } : u
      ));
      if (selectedUser?.iduser === user.iduser) {
        setSelectedUser({ ...selectedUser, is_blocked: newStatus });
      }
    } catch (error) {
      alert(`Erreur lors de l'action ${action}`);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const openCreateEspaceModal = (user) => {
    setEspaceModalUser(user);
    setEspaceNameInput('');
  };

  const closeCreateEspaceModal = () => {
    setEspaceModalUser(null);
    setEspaceNameInput('');
    setCreatingEspace(false);
  };

  const submitCreateEspace = async (e) => {
    e.preventDefault();
    if (!espaceNameInput.trim()) {
      showToast("Le nom de l'espace est obligatoire.", 'error');
      return;
    }

    setCreatingEspace(true);
    try {
      await api.post(`/dashboard-admin/users/${espaceModalUser.iduser}/create-espace`, { nom: espaceNameInput.trim() });
      showToast(`Espace "${espaceNameInput.trim()}" créé avec succès. ${espaceModalUser.username || espaceModalUser.email} en est l'Admin général.`);
      if (isGroupsExpanded) fetchUserGroups(espaceModalUser.iduser);
      closeCreateEspaceModal();
    } catch (error) {
      showToast(error.response?.data?.error || "Erreur lors de la création de l'espace", 'error');
      setCreatingEspace(false);
    }
  };

  const handleRemoveFromGroup = async (userId, groupId) => {
    if (window.confirm('Retirer l\'utilisateur de ce groupe ?')) {
      try {
        await api.delete(`/admin/users/${userId}/groups/${groupId}`);
        setUserGroups(userGroups.filter(g => g.idgroupe !== groupId));
      } catch (error) {
        alert('Erreur lors du retrait du groupe');
      }
    }
  };

  const viewUserDetails = (user) => {
    setSelectedUser(user);
    setIsEditingUser(false);
    setIsGroupsExpanded(false); // Reset to collapsed
    setExpandedGroupIds([]); // Reset individual groups
    fetchUserGroups(user.iduser);
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setIsEditingUser(false);
    setEditForm(null);
  };

  const startEditUser = () => {
    setEditForm({
      firstname: selectedUser.firstname || '',
      lastname: selectedUser.lastname || '',
      username: selectedUser.username || '',
      phone: selectedUser.phone || '',
      country: selectedUser.country || '',
      poste: selectedUser.poste || '',
      description: selectedUser.description || ''
    });
    setIsEditingUser(true);
  };

  const cancelEditUser = () => {
    setIsEditingUser(false);
    setEditForm(null);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const saveUserProfile = async () => {
    if (!editForm.firstname.trim() || !editForm.lastname.trim() || !editForm.username.trim()) {
      alert("Prénom, nom et nom d'utilisateur sont obligatoires.");
      return;
    }
    if (/\s/.test(editForm.username)) {
      alert("Le nom d'utilisateur ne doit pas contenir d'espaces.");
      return;
    }

    setSavingProfile(true);
    try {
      const response = await api.put(`/admin/users/${selectedUser.iduser}`, editForm);
      const updatedUser = response.data.user;
      setUsers(users.map(u => u.iduser === updatedUser.iduser ? { ...u, ...updatedUser } : u));
      setSelectedUser(prev => ({ ...prev, ...updatedUser }));
      setIsEditingUser(false);
      setEditForm(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour du profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroupIds(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesDate = true;
    if (dateFrom || dateTo) {
      const created = new Date(user.created_at || user.date_inscription);
      created.setHours(0, 0, 0, 0);
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (created < from) matchesDate = false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (created > to) matchesDate = false;
      }
    }
    
    return matchesSearch && matchesDate;
  }) : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  // Reset to page 1 when search term or date filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFrom, dateTo]);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return (
      <div className="pagination">
        <button 
          className="pagination-btn" 
          onClick={() => goToPage(1)} 
          disabled={currentPage === 1}
        >
          ««
        </button>
        <button 
          className="pagination-btn" 
          onClick={() => goToPage(currentPage - 1)} 
          disabled={currentPage === 1}
        >
          «
        </button>
        
        {startPage > 1 && (
          <>
            <button className="pagination-btn" onClick={() => goToPage(1)}>1</button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}
        
        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
          <button
            key={page}
            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            onClick={() => goToPage(page)}
          >
            {page}
          </button>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button className="pagination-btn" onClick={() => goToPage(totalPages)}>{totalPages}</button>
          </>
        )}
        
        <button 
          className="pagination-btn" 
          onClick={() => goToPage(currentPage + 1)} 
          disabled={currentPage === totalPages}
        >
          »
        </button>
        <button 
          className="pagination-btn" 
          onClick={() => goToPage(totalPages)} 
          disabled={currentPage === totalPages}
        >
          »»
        </button>
        
        <span className="pagination-info">
          {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} sur {filteredUsers.length}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des utilisateurs...</p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Gestion des Utilisateurs</h1>
        <p>Gérer et surveiller tous les utilisateurs de la plateforme</p>
      </div>

      <div className="users-stats">
        <div className="stat-box">
          <h3>Total Utilisateurs</h3>
          <p className="stat-number">{users.length}</p>
        </div>
        <div className="stat-box">
          <h3>Utilisateurs Vérifiés</h3>
          <p className="stat-number">{users.filter(u => u.is_verified).length}</p>
        </div>
        <div className="stat-box">
          <h3>Utilisateurs Bloqués</h3>
          <p className="stat-number warning">{users.filter(u => u.is_blocked).length}</p>
        </div>
      </div>

      <div className="search-bar">
        <FontAwesomeIcon icon={faSearch} />
        <input
          type="text"
          placeholder="Rechercher par email, nom, prénom..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Date Range Filter */}
      <div className="date-range-filter">
        <FontAwesomeIcon icon={faCalendar} className="date-filter-icon" />
        <span className="date-range-label">Période :</span>
        <div className="date-range-inputs">
          <input
            type="date"
            className="date-filter-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            title="Date de début"
            placeholder="Du"
          />
          <span className="date-range-separator">→</span>
          <input
            type="date"
            className="date-filter-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            title="Date de fin"
            placeholder="Au"
          />
        </div>
        {(dateFrom || dateTo) && (
          <>
            <span className="date-range-count">{filteredUsers.length} résultat{filteredUsers.length !== 1 ? 's' : ''}</span>
            <button className="date-filter-clear" onClick={() => { setDateFrom(''); setDateTo(''); }} title="Effacer le filtre">×</button>
          </>
        )}
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Contact</th>
              <th>Statut</th>
              <th>Date d'inscription</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map(user => (
              <React.Fragment key={user.iduser}>
              <tr 
                className={`${user.is_blocked ? 'blocked-row' : ''} user-table-row ${expandedUserId === user.iduser ? 'expanded' : ''}`}
                onClick={() => toggleUserExpand(user.iduser)}
              >
                <td>
                  <div className="user-info-cell">
                    <div className="user-avatar">
                      {user.firstname ? user.firstname[0].toUpperCase() : 'U'}
                    </div>
                    <div>
                      <div className="user-name">
                        {user.firstname} {user.lastname}
                        {isNewUser(user) && <span className="nouveau-tag">Nouveau</span>}
                      </div>
                      <div className="user-username">@{user.username}</div>
                      <div className="user-email-mobile">{user.email}</div>
                    </div>
                  </div>
                </td>
                <td className="contact-col">
                  <div className="contact-info">
                    <div><FontAwesomeIcon icon={faEnvelope} /> {user.email}</div>
                    {user.phone && <div><FontAwesomeIcon icon={faPhone} /> {user.phone}</div>}
                  </div>
                </td>
                <td>
                  <div className="status-badges">
                    {user.is_verified ? (
                      <span className="badge verified"><FontAwesomeIcon icon={faCheckCircle} /> Vérifié</span>
                    ) : (
                      <span className="badge unverified"><FontAwesomeIcon icon={faTimesCircle} /> Non vérifié</span>
                    )}
                    {user.is_blocked && (
                      <span className="badge blocked"><FontAwesomeIcon icon={faBan} /> Bloqué</span>
                    )}
                  </div>
                </td>
                <td className="date-col">
                  <div className="date-info">
                    <FontAwesomeIcon icon={faCalendar} />
                    {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </td>
                <td className="actions-col">
                  <div className="actions-cell">
                    <button 
                      className="btn-icon view" 
                      onClick={(e) => { e.stopPropagation(); viewUserDetails(user); }}
                      title="Voir détails"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                    {canBlockUsers && (
                      <button 
                        className={`btn-icon ${user.is_blocked ? 'unblock' : 'block'}`}
                        onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}
                        title={user.is_blocked ? "Débloquer" : "Bloquer"}
                      >
                        <FontAwesomeIcon icon={user.is_blocked ? faUnlock : faBan} />
                      </button>
                    )}
                    {canDeleteUsers && (
                      <button 
                        className="btn-icon delete" 
                        onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.iduser, `${user.firstname || ''} ${user.lastname || ''} (${user.username || user.email})`); }}
                        title="Supprimer"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {/* Mobile expanded detail row */}
              {expandedUserId === user.iduser && (
                <tr className="user-expanded-row">
                  <td colSpan="5">
                    <div className="user-expanded-card">
                      <div className="expanded-detail-grid">
                        <div className="expanded-detail-item">
                          <span className="expanded-label"><FontAwesomeIcon icon={faEnvelope} /> Email</span>
                          <span className="expanded-value">{user.email}</span>
                        </div>
                        {user.phone && (
                          <div className="expanded-detail-item">
                            <span className="expanded-label"><FontAwesomeIcon icon={faPhone} /> Téléphone</span>
                            <span className="expanded-value">{user.phone}</span>
                          </div>
                        )}
                        <div className="expanded-detail-item">
                          <span className="expanded-label"><FontAwesomeIcon icon={faCalendar} /> Inscrit le</span>
                          <span className="expanded-value">{new Date(user.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="expanded-detail-item">
                          <span className="expanded-label"><FontAwesomeIcon icon={faUser} /> ID</span>
                          <span className="expanded-value expanded-id">#{user.iduser}</span>
                        </div>
                      </div>
                      <div className="expanded-actions">
                        <button className="expanded-action-btn view" onClick={(e) => { e.stopPropagation(); viewUserDetails(user); }}>
                          <FontAwesomeIcon icon={faEye} /> Détails
                        </button>
                        {canBlockUsers && (
                          <button className={`expanded-action-btn ${user.is_blocked ? 'unblock' : 'block'}`} onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}>
                            <FontAwesomeIcon icon={user.is_blocked ? faUnlock : faBan} /> {user.is_blocked ? 'Débloquer' : 'Bloquer'}
                          </button>
                        )}
                        {canDeleteUsers && (
                          <button className="expanded-action-btn delete" onClick={(e) => { e.stopPropagation(); handleDeleteUser(user.iduser, `${user.firstname || ''} ${user.lastname || ''}`); }}>
                            <FontAwesomeIcon icon={faTrash} /> Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {renderPagination()}
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={closeUserModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditingUser ? "Modifier l'utilisateur" : "Détails de l'utilisateur"}</h2>
              <button className="close-btn" onClick={closeUserModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="user-profile-header">
                <div className="large-avatar">
                  {selectedUser.firstname ? selectedUser.firstname[0].toUpperCase() : 'U'}
                </div>
                <div style={{ flex: 1 }}>
                  {isEditingUser ? (
                    <div className="edit-name-row">
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.firstname}
                        onChange={(e) => handleEditFormChange('firstname', e.target.value)}
                        placeholder="Prénom"
                      />
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.lastname}
                        onChange={(e) => handleEditFormChange('lastname', e.target.value)}
                        placeholder="Nom"
                      />
                    </div>
                  ) : (
                    <h3>{selectedUser.firstname} {selectedUser.lastname}</h3>
                  )}
                  {isEditingUser ? (
                    <div className="edit-username-row">
                      <span>@</span>
                      <input
                        type="text"
                        className="edit-input"
                        value={editForm.username}
                        onChange={(e) => handleEditFormChange('username', e.target.value)}
                        placeholder="nom_utilisateur"
                      />
                    </div>
                  ) : (
                    <p>@{selectedUser.username}</p>
                  )}
                  <div className="modal-badges">
                    {selectedUser.is_verified ? (
                      <span className="badge verified">Vérifié</span>
                    ) : (
                      <span className="badge unverified">Non vérifié</span>
                    )}
                    {selectedUser.is_blocked && (
                      <span className="badge blocked">Bloqué</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="details-grid">
                <div className="detail-item">
                  <label>Email <small className="text-muted">(non modifiable)</small></label>
                  <p>{selectedUser.email}</p>
                </div>
                <div className="detail-item">
                  <label>Téléphone</label>
                  {isEditingUser ? (
                    <input
                      type="text"
                      className="edit-input"
                      value={editForm.phone}
                      onChange={(e) => handleEditFormChange('phone', e.target.value)}
                    />
                  ) : (
                    <p>{selectedUser.phone || 'Non renseigné'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Pays</label>
                  {isEditingUser ? (
                    <input
                      type="text"
                      className="edit-input"
                      value={editForm.country}
                      onChange={(e) => handleEditFormChange('country', e.target.value)}
                    />
                  ) : (
                    <p>{selectedUser.country || 'Non renseigné'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Poste</label>
                  {isEditingUser ? (
                    <input
                      type="text"
                      className="edit-input"
                      value={editForm.poste}
                      onChange={(e) => handleEditFormChange('poste', e.target.value)}
                    />
                  ) : (
                    <p>{selectedUser.poste || 'Non renseigné'}</p>
                  )}
                </div>
                <div className="detail-item">
                  <label>Inscrit le</label>
                  <p>{new Date(selectedUser.created_at).toLocaleString()}</p>
                </div>
                <div className="detail-item">
                  <label>Dernière connexion</label>
                  <p>{selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Jamais'}</p>
                </div>
                {isEditingUser && (
                  <div className="detail-item full-width">
                    <label>Description</label>
                    <textarea
                      className="edit-input edit-textarea"
                      value={editForm.description}
                      onChange={(e) => handleEditFormChange('description', e.target.value)}
                      rows={3}
                    />
                  </div>
                )}
                {selectedUser.is_blocked && (
                  <div className="detail-item full-width blocked-reason">
                    <label className="text-danger">Motif du blocage</label>
                    <p className="text-danger">{selectedUser.block_reason || 'Aucun motif spécifié'}</p>
                  </div>
                )}
              </div>

              <div className="groups-section">
                <h4 
                  onClick={() => setIsGroupsExpanded(!isGroupsExpanded)} 
                  style={{cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                >
                  <span><FontAwesomeIcon icon={faLayerGroup} /> Groupes ({userGroups.length})</span>
                  <FontAwesomeIcon icon={isGroupsExpanded ? faChevronUp : faChevronDown} />
                </h4>
                
                {isGroupsExpanded && (
                  loadingGroups ? (
                    <p>Chargement des groupes...</p>
                  ) : userGroups.length > 0 ? (
                    <div className="groups-list">
                      {userGroups.map(group => (
                        <div key={group.idgroupe} className="group-item-container">
                          <div className="group-item">
                            <div 
                              className="group-info" 
                              onClick={() => toggleGroup(group.idgroupe)}
                              style={{cursor: 'pointer', flex: 1}}
                            >
                              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <FontAwesomeIcon 
                                  icon={expandedGroupIds.includes(group.idgroupe) ? faChevronUp : faChevronDown} 
                                  style={{fontSize: '0.8rem', color: 'var(--text-secondary)'}}
                                />
                                <span className="group-name">{group.nom}</span>
                              </div>
                              <span className="group-role">
                                {group.user_role === 'admin' ? 'Administrateur' : 'Membre'} 
                                • Rejoint le {new Date(group.joined_at || group.date_adhesion).toLocaleDateString()}
                              </span>
                            </div>
                            <button 
                              className="btn-remove-group"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFromGroup(selectedUser.iduser, group.idgroupe);
                              }}
                              title="Retirer du groupe"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                          {expandedGroupIds.includes(group.idgroupe) && group.privileges && group.privileges.length > 0 && (
                            <div className="group-privileges">
                              <small>Privilèges:</small>
                              <div className="privilege-tags">
                                {group.privileges.map((priv, idx) => (
                                  <span key={idx} className="privilege-tag">
                                    {priv.display_name || priv.privilege_name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-data">Aucun groupe</p>
                  )
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              {isEditingUser ? (
                <>
                  <button className="btn" onClick={cancelEditUser} disabled={savingProfile}>
                    Annuler
                  </button>
                  <button className="btn btn-primary" onClick={saveUserProfile} disabled={savingProfile}>
                    {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </>
              ) : (
                <>
                  {canManageUsers && (
                    <button className="btn btn-primary" onClick={startEditUser}>
                      Modifier le profil
                    </button>
                  )}
                  {canManageGroups && (
                    <button className="btn btn-primary" onClick={() => openCreateEspaceModal(selectedUser)}>
                      Créer un espace
                    </button>
                  )}
                  <button
                    className={`btn ${selectedUser.is_blocked ? 'btn-success' : 'btn-warning'}`}
                    onClick={() => handleBlockUser(selectedUser)}
                  >
                    {selectedUser.is_blocked ? 'Débloquer le compte' : 'Bloquer le compte'}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDeleteUser(selectedUser.iduser, `${selectedUser.firstname || ''} ${selectedUser.lastname || ''} (${selectedUser.username || selectedUser.email})`)}
                  >
                    Supprimer le compte
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Espace Modal (replaces window.prompt) */}
      {espaceModalUser && (
        <div className="modal-overlay" onClick={closeCreateEspaceModal}>
          <div className="modal-content espace-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Créer un espace</h2>
              <button className="close-btn" onClick={closeCreateEspaceModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form onSubmit={submitCreateEspace}>
              <div className="modal-body">
                <p className="espace-modal-hint">
                  Pour <strong>{espaceModalUser.username || espaceModalUser.email}</strong> — cet utilisateur en deviendra l'Admin général.
                </p>
                <div className="form-group">
                  <label htmlFor="espaceNameInput">Nom de l'espace</label>
                  <input
                    id="espaceNameInput"
                    type="text"
                    className="edit-input"
                    autoFocus
                    value={espaceNameInput}
                    onChange={(e) => setEspaceNameInput(e.target.value)}
                    placeholder="Ex: Équipe Marketing, Projet Alpha..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={closeCreateEspaceModal} disabled={creatingEspace}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={creatingEspace}>
                  {creatingEspace ? 'Création...' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <FontAwesomeIcon icon={toast.type === 'error' ? faTimesCircle : faCheckCircle} />
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default UsersDashboard;
